import React from "react";
import { captureMessage } from "@sentry/browser";
import { useModal } from "react-modal-hook";
import { canDeleteComment } from "common/authorization/customPolicies";
import { ActivityTargetType, ActivityType } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { Icon } from "../Common/Icons/LucideIcons";
import Modal from "../Common/Modal";
import {
  ActivityRow,
  Container,
  DeleteActivity,
  IconAndText,
  TextContainer,
} from "./__styles__/ListItem";
import Bullet from "./Bullet";
import Comment from "./Comment";
import { DeleteActivityConfirmation } from "./DeleteActivityConfirmation";
import Message from "./Message";
import { FeedSource } from "./MessagePartBuilders/types";
import {
  Activity,
  isAdminActivity,
  isApiActivity,
  isCommentActivity,
  isGuestActivity,
  isPublicUserActivity,
  isUserActivity,
} from "./types";

interface ListItemProps {
  activity: Activity;
  onUpdate: (deletedId?: string) => void;
  linkToObject?: boolean;
  truncateComments: boolean;
  allowEdits: boolean;
  feedSource: FeedSource;
}

const ListItem = ({
  activity,
  onUpdate,
  linkToObject,
  feedSource,
  truncateComments,
  allowEdits,
}: ListItemProps) => {
  const { user, admin, account } = React.useContext(AuthContext);
  const [showDeleteActivityModal, hideDeleteActivityModal] = useModal(() => (
    <Modal onRequestClose={hideDeleteActivityModal}>
      <DeleteActivityConfirmation
        activity={activity}
        closeModal={hideDeleteActivityModal}
        onUpdate={onUpdate}
      />
    </Modal>
  ));

  const currentUser = user || admin;
  let creator: string;
  if (
    isAdminActivity(activity) ||
    (activity.targetType === ActivityTargetType.CERTIFICATE &&
      activity.type === ActivityType.CREATED)
  ) {
    creator = "Forerunner";
  } else if (isGuestActivity(activity)) {
    creator = "Guest";
  } else if (isApiActivity(activity)) {
    creator = activity.createdBy?.name ?? "API";
  } else if (isUserActivity(activity) && activity.createdBy) {
    const isCreator = activity.createdBy.id === currentUser?.id;
    creator =
      isCreator ? "You" : (
        `${activity.createdBy.firstName} ${activity.createdBy.lastName}`
      );
  } else if (isPublicUserActivity(activity)) {
    creator = `${activity.createdBy.firstName} ${activity.createdBy.lastName}`;
  } else {
    captureMessage(`Unknown activity creator type for activity ${activity.id}`);
    creator = "Forerunner";
  }

  if (activity.isGroup && !activity.targetInfo.totalCount) {
    return null;
  }

  return (
    <Container>
      <ActivityRow>
        <IconAndText>
          <Bullet activity={activity} />
          <TextContainer>
            <Message
              creator={creator}
              activity={activity}
              linkToObject={linkToObject}
              feedSource={feedSource}
            />
          </TextContainer>
        </IconAndText>
        {currentUser?.role.name === "admin" &&
          !(isCommentActivity(activity) && feedSource === "Property") && (
            <DeleteActivity
              data-testid="deleteActivity"
              onClick={showDeleteActivityModal}
            >
              <Icon iconName="x" color="contentSecondary" size="16px" />
            </DeleteActivity>
          )}
        {canDeleteComment({
          user: {
            id: currentUser!.id,
            role: currentUser!.role.name,
            accountId: account!.id,
          },
          comment: {
            createdBy: activity!.createdBy!,
            accountId: account!.id,
          },
        }) &&
          allowEdits &&
          isCommentActivity(activity) && (
            <DeleteActivity
              data-testid="deleteActivity"
              onClick={showDeleteActivityModal}
            >
              <Icon iconName="x" color="contentSecondary" size="16px" />
            </DeleteActivity>
          )}
      </ActivityRow>
      {isCommentActivity(activity) && activity.target && (
        <Comment
          content={activity.target.content}
          truncate={truncateComments}
        />
      )}
    </Container>
  );
};

export default ListItem;
